import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useCurrentFamilyContext } from 'src/clients/context/useCurrentFamilyContext';
import { useClient } from 'src/clients/hooks/useClient';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useAppContainerContext } from './AppContainerContext';
import { useChatMessages } from './chat/hooks/useChatMessages';
import { ChatMode, ChatState, Message, StoredMessage } from './chat/types';
import { ChatFile } from './chats/types';
import { PreviousChatContext } from './sidebar/DisplacedChatBanner';
import { useDisplacedChat } from './sidebar/useDisplacedChat';
import { useAppContainer } from './useAppContainer';

type ChatScope = {
  familyModeActive: boolean;
  familyId: string | null;
  clientId: string | null;
};

type SiestaContextValue = {
  messages: Message[];
  latestIncomingId: string | null;
  currentChatState: ChatState | null;
  previousChatContext: PreviousChatContext | null;
  usedActionIds: Set<string>;
  chatScope: ChatScope;
  sendUserMessage: (
    content: string,
    clientId: string | null,
    conversationId: string,
    attachedFiles?: ChatFile[],
    verify?: { sectionPath?: string[] },
    silent?: boolean,
  ) => Promise<void>;
  resumeActiveTurn: (conversationId: string, turnId: string) => void;
  clearMessages: () => void;
  loadHistory: (rawMessages: StoredMessage[]) => void;
  replaceLastAssistantMessage: (content: string) => void;
  appendInterruptedMarker: () => void;
  finaliseLimitReachedMessage: (id: string) => void;
  cancelStreaming: () => void;
  stopGeneration: () => void;
  resetIfDisplaced: () => string;
  discardChat: () => string;
  adoptDisplacedChat: () => void;
  goBack: () => void;
  markActionUsed: (id: string) => void;
  clearUsedActionIds: () => void;
};

const SiestaContext = createContext<SiestaContextValue | undefined>(undefined);

export function SiestaProvider({ children }: { children: React.ReactNode }) {
  const { contentMeta } = useAppContainerContext();
  const { siestaMode } = useAppContainer();
  const {
    flagEnabled,
    clientId: routeClientId,
    householdFamilyId,
    isHouseholdView,
  } = useCurrentFamilyContext();

  // On a member's own page, resolve their household so the chat runs over the whole family with that
  // member in focus. The conversation is family-scoped from the first message, so moving to the
  // family view (or another member) continues the same thread — nothing is reparented.
  // Mirrors useCurrentClientIfAvailable: the single-client fetch only carries familyId when
  // paginated clients is on, so fall back to the client list otherwise.
  const { flagEnabledPaginatedClients } = useFlags();
  const wantsClientFamily = Boolean(flagEnabled && !isHouseholdView && routeClientId);
  const single = useClient(
    wantsClientFamily && flagEnabledPaginatedClients ? (routeClientId ?? undefined) : undefined,
  );
  const list = useClients({}, { enabled: wantsClientFamily && !flagEnabledPaginatedClients });
  const resolvedClient = flagEnabledPaginatedClients
    ? single.client
    : (list.clients.find(c => c.id === routeClientId) ?? null);
  const clientFamilyId = wantsClientFamily ? (resolvedClient?.familyId ?? null) : null;
  const resolvedFamilyId = isHouseholdView ? householdFamilyId : clientFamilyId;

  // A member's client detail can be briefly uncached on first navigation, leaving its household
  // momentarily unknown. Hold the last known household across that gap so navigating within a family
  // doesn't flicker the chat's scope and wrongly displace the thread — but only onto that household's
  // own members: bridging to an unrelated client must not briefly scope the chat to the previous
  // family. While the held family itself is loading (it's cached from the chat just held), keep it
  // rather than flicker.
  const clientResolving =
    wantsClientFamily && (flagEnabledPaginatedClients ? single.isLoading : list.isLoading);
  const lastFamilyIdRef = useRef<string | null>(null);
  const heldFamilyId = clientResolving ? lastFamilyIdRef.current : null;
  const { family: heldFamily } = useFamily(heldFamilyId ?? undefined);
  const heldCoversRoute =
    heldFamilyId !== null &&
    (routeClientId === null ||
      heldFamily === null ||
      heldFamily.members.some(member => member.clientId === routeClientId));
  const familyId = resolvedFamilyId ?? (heldCoversRoute ? heldFamilyId : null);
  useEffect(() => {
    lastFamilyIdRef.current = familyId;
  }, [familyId]);

  const familyModeActive = Boolean(flagEnabled && familyId);
  // When focused on a member's page (not the whole-household view), tell Siesta which member so bare
  // questions default to them.
  const focusedClientId = familyModeActive && !isHouseholdView ? routeClientId : null;
  const clientId = familyModeActive ? null : routeClientId;

  const isDocumentMode = contentMeta?.chatMode === ChatMode.DOCUMENT;
  const requestBodyBuilder = (
    message: string,
    activeConversationId: string,
    verify?: { sectionPath?: string[] },
  ) => ({
    conversationId: activeConversationId,
    documentContent:
      contentMeta?.getDocumentContent?.() ?? contentMeta?.metadata?.documentContent ?? '',
    editInstruction: message,
    clientId,
    ...(verify ? { verifyClaim: message, verifySectionPath: verify.sectionPath } : {}),
  });
  const resolveChatOptions = (): Parameters<typeof useChatMessages>[0] => {
    if (isDocumentMode) {
      return {
        mode: ChatMode.DOCUMENT,
        entityId: contentMeta?.entityId ?? '',
        onStateChange: contentMeta?.onChatStateChange,
        requestBodyBuilder,
      };
    }
    if (familyModeActive && familyId) {
      return {
        mode: ChatMode.FAMILY,
        familyId,
        focusedClientId,
        onStateChange: contentMeta?.onChatStateChange,
      };
    }
    return { mode: ChatMode.CLIENT, onStateChange: contentMeta?.onChatStateChange };
  };
  const chat = useChatMessages(resolveChatOptions());

  const [usedActionIds, setUsedActionIds] = useState<Set<string>>(new Set());
  const markActionUsed = (id: string) => setUsedActionIds(prev => new Set(prev).add(id));
  const clearUsedActionIds = () => setUsedActionIds(new Set());

  const tracking = useDisplacedChat({
    messageCount: chat.messages.length,
    clientId: (familyModeActive ? familyId : clientId) ?? undefined,
    chatMode: familyModeActive ? ChatMode.FAMILY : contentMeta?.chatMode,
    entityId: (familyModeActive ? familyId : contentMeta?.entityId) ?? undefined,
    clearMessages: chat.clearMessages,
    clearUsedActionIds,
    cancelStreaming: chat.cancelStreaming,
  });

  const chatScope: ChatScope = { familyModeActive, familyId, clientId };

  useEffect(() => {
    if (siestaMode !== 'chat' && siestaMode !== 'busy') {
      chat.cancelStreaming();
    }
  }, [siestaMode, chat.cancelStreaming]);

  const chatActionsRef = contentMeta?.chatActionsRef;
  useEffect(() => {
    if (chatActionsRef) {
      chatActionsRef.current = {
        replaceLastAssistantMessage: chat.replaceLastAssistantMessage,
        // resetIfDisplaced gives the active conversation id (same discipline as the other sends).
        sendVerifyMessage: (claim, sectionPath) =>
          void chat.sendUserMessage(claim, clientId, tracking.resetIfDisplaced(), [], {
            sectionPath,
          }),
      };
    }
    return () => {
      if (chatActionsRef) chatActionsRef.current = null;
    };
  }, [
    chatActionsRef,
    chat.replaceLastAssistantMessage,
    chat.sendUserMessage,
    tracking.resetIfDisplaced,
    clientId,
  ]);

  return (
    <SiestaContext
      value={{ ...chat, ...tracking, usedActionIds, markActionUsed, clearUsedActionIds, chatScope }}
    >
      {children}
    </SiestaContext>
  );
}

export function useSiesta() {
  const ctx = useContext(SiestaContext);
  if (ctx === undefined) {
    throw new Error('useSiesta must be used within a SiestaProvider');
  }
  return ctx;
}
